import Row from '@sdk/components/ui/spacing/Row';
import Column from '@sdk/components/ui/spacing/Column';
import gettext from '@sdk/lang/gettext';
import useAuth from '@sdk/authentication/useAuth';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Text14 from '@sdk/components/ui/typography/Text14';
import Text11 from '@sdk/components/ui/typography/Text11';
import {
  AutomatedBreaksWaivingBreakMode,
  AutomatedBreaksWaivingMode,
} from '@sdk/authentication/types/apiTypes';
import Picker from '@sdk/components/forms/Picker';
import { AutomatedBreakType } from '@sdk/settings/types';

import { useWaivingSettings } from '@web/settings/components/schedule/Breaks/WaivingSettings/waivingSettings.hooks';
import { FIELD_WIDTH_205, FIELD_WIDTH_100 } from '@web/settings/constants';
import { CustomTextField } from '@web/onboarding/Checklist/components/CustomTextField';
import {
  AUTOMATED_BREAKS_WAIVING_MODE_OPTIONS,
  AUTOMATED_BREAKS_WAIVING_BREAK_OPTIONS,
} from '@web/settings/components/schedule/Breaks/breaks.consts';
import SettingsSwitcherRow from '@web/settings/components/SettingsSwitcherRow';
import { AutomatedBreakFormFields } from '@web/settings/components/schedule/Breaks/breaks.types';
import { ErrorRow } from '@web/settings/components/schedule/Breaks/WaivingSettings/waivingSettings.styles';

type Props = {
  fields: AutomatedBreakFormFields;
  automatedBreakType: AutomatedBreakType;
};

export const WaivingSettings = ({ fields, automatedBreakType }: Props) => {
  const { currentUser } = useAuth();
  const {
    updateWaivingEnabled,
    updateWaivingMode,
    updateWaivingFirstBreakMode,
    updateWaivingSecondBreakMode,
    updateWaivingFirstBreakLessThanHours,
    updateWaivingSecondBreakLessThanHours,
  } = useWaivingSettings({ fields, automatedBreakType });

  return (
    <>
      <SettingsSwitcherRow
        mTop={20}
        title={gettext('Waiver')}
        subtitle={gettext('Allow employees to waive %(automatedBreakType)s breaks.', {
          automatedBreakType,
        })}
        value={fields.isWaivingEnabled.value}
        onChange={updateWaivingEnabled}
        disabled={!currentUser.isAdmin}
      />

      {automatedBreakType === AutomatedBreakType.meal ? (
        <>
          {fields.isWaivingEnabled.value ? (
            <Row alignItems="center" justifyContent="space-between" mTop={20}>
              <Column>
                <Text14 themeColor={ThemeColorType.gray}>{gettext('Allow waivers for')}</Text14>
              </Column>

              <Picker
                options={AUTOMATED_BREAKS_WAIVING_MODE_OPTIONS}
                value={fields.waivingMode.value}
                customWidth={FIELD_WIDTH_205}
                onChange={updateWaivingMode}
                disabled={!currentUser.isAdmin}
              />
            </Row>
          ) : null}

          {fields.isWaivingEnabled.value &&
          [
            AutomatedBreaksWaivingMode.FIRST_BREAK_ONLY,
            AutomatedBreaksWaivingMode.FIRST_AND_SECOND_BREAK,
          ].includes(fields.waivingMode.value) ? (
            <Column>
              <Row alignItems="center" mTop={20}>
                <Column mRight={10}>
                  <Text14 themeColor={ThemeColorType.gray}>
                    {gettext('Enable waiver for the 1st meal break')}
                  </Text14>
                </Column>

                <Picker
                  options={AUTOMATED_BREAKS_WAIVING_BREAK_OPTIONS}
                  value={fields.waivingFirstBreakMode.value}
                  customWidth={FIELD_WIDTH_100}
                  onChange={updateWaivingFirstBreakMode}
                  disabled={!currentUser.isAdmin}
                />

                {fields.waivingFirstBreakMode.value ===
                AutomatedBreaksWaivingBreakMode.IF_EMPLOYEE_WORKS_LESS_THAN_HOURS ? (
                  <>
                    <Column mLeft={10}>
                      <Text14 themeColor={ThemeColorType.gray}>
                        {gettext('employees work less than')}
                      </Text14>
                    </Column>

                    <Column mLeft={10}>
                      <CustomTextField
                        width={FIELD_WIDTH_100}
                        skipErrorMessage
                        maxLength={5}
                        value={fields.waivingFirstBreakLessThanHours.value}
                        onChange={updateWaivingFirstBreakLessThanHours}
                        invalid={!!fields.waivingFirstBreakLessThanHours.error}
                        onBlur={() => fields.waivingFirstBreakLessThanHours.runValidation()}
                        disabled={!currentUser.isAdmin}
                      />
                    </Column>

                    <Column mLeft={10}>
                      <Text14 themeColor={ThemeColorType.gray}>{gettext('hours')}</Text14>
                    </Column>
                  </>
                ) : null}
              </Row>
              {fields.waivingFirstBreakMode.value ===
                AutomatedBreaksWaivingBreakMode.IF_EMPLOYEE_WORKS_LESS_THAN_HOURS &&
                fields.waivingFirstBreakLessThanHours.error && (
                  <ErrorRow mTop={5}>
                    <Text11 themeColor={ThemeColorType.danger} bold>
                      {fields.waivingFirstBreakLessThanHours.error}
                    </Text11>
                  </ErrorRow>
                )}
            </Column>
          ) : null}

          {fields.isWaivingEnabled.value &&
          [
            AutomatedBreaksWaivingMode.SECOND_BREAK_ONLY,
            AutomatedBreaksWaivingMode.FIRST_AND_SECOND_BREAK,
          ].includes(fields.waivingMode.value) ? (
            <Column>
              <Row alignItems="center" mTop={20}>
                <Column mRight={10}>
                  <Text14 themeColor={ThemeColorType.gray}>
                    {gettext('Enable waiver for the 2nd meal break')}
                  </Text14>
                </Column>

                <Picker
                  options={AUTOMATED_BREAKS_WAIVING_BREAK_OPTIONS}
                  value={fields.waivingSecondBreakMode.value}
                  customWidth={FIELD_WIDTH_100}
                  onChange={updateWaivingSecondBreakMode}
                  disabled={!currentUser.isAdmin}
                />

                {fields.waivingSecondBreakMode.value ===
                AutomatedBreaksWaivingBreakMode.IF_EMPLOYEE_WORKS_LESS_THAN_HOURS ? (
                  <>
                    <Column mLeft={10}>
                      <Text14 themeColor={ThemeColorType.gray}>
                        {gettext('employees work less than')}
                      </Text14>
                    </Column>

                    <Column mLeft={10}>
                      <CustomTextField
                        width={FIELD_WIDTH_100}
                        skipErrorMessage
                        maxLength={5}
                        value={fields.waivingSecondBreakLessThanHours.value}
                        onChange={updateWaivingSecondBreakLessThanHours}
                        invalid={!!fields.waivingSecondBreakLessThanHours.error}
                        onBlur={() => fields.waivingSecondBreakLessThanHours.runValidation()}
                        disabled={!currentUser.isAdmin}
                      />
                    </Column>

                    <Column mLeft={10}>
                      <Text14 themeColor={ThemeColorType.gray}>{gettext('hours')}</Text14>
                    </Column>
                  </>
                ) : null}
              </Row>
              {fields.waivingSecondBreakMode.value ===
                AutomatedBreaksWaivingBreakMode.IF_EMPLOYEE_WORKS_LESS_THAN_HOURS &&
                fields.waivingSecondBreakLessThanHours.error && (
                  <ErrorRow mTop={5}>
                    <Text11 themeColor={ThemeColorType.danger} bold>
                      {fields.waivingSecondBreakLessThanHours.error}
                    </Text11>
                  </ErrorRow>
                )}
            </Column>
          ) : null}
        </>
      ) : null}
    </>
  );
};
